import React, { useCallback, useEffect, useState } from "react";
import toast from "react-hot-toast";
import { useAuth } from "../../hooks/useAuth";
import { listFacilityBlocks, createFacilityBlock, deleteFacilityBlock } from "../../services/facilityBlockService";
import { listFacilities, getFacility } from "../../services/facilityService";
import {
  listFacilityBlockRequests,
  listMyFacilityBlockRequests,
  getFacilityBlockRequest,
  submitFacilityBlockRequest,
  submitFacilityUnblockRequest,
  approveFacilityBlockRequest,
  rejectFacilityBlockRequest,
} from "../../services/facilityBlockRequestService";
import {
  TOKENS,
  Icon,
  ICONS,
  Pill,
  EmptyState,
  Spinner,
  inputClass,
  labelClass,
  btnPrimary,
  btnSecondary,
  Pagination,
  usePagination,
} from "../../components/common/DashboardUI";

// ── Modal shell ─────────────────────────────────────────────────────────────
function Modal({ title, onClose, children, size = "md" }) {
  const width = size === "lg" ? "max-w-2xl" : "max-w-lg";
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      onClick={onClose}
    >
      <div
        className={`relative w-full ${width} bg-white rounded-2xl shadow-2xl p-6 max-h-[90vh] overflow-y-auto`}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="absolute top-3 right-3 w-9 h-9 rounded-full flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition"
        >
          <Icon path={ICONS.x} className="w-5 h-5" width={2} />
        </button>
        <h2 className="text-lg font-bold mb-4 pr-8" style={{ color: TOKENS.NAVY }}>
          {title}
        </h2>
        {children}
      </div>
    </div>
  );
}

// Pull the message, field errors and (for the booking-conflict case) the
// conflicting booking ids out of an axios error.
function errInfo(err, fallback) {
  const data = err?.response?.data;
  return {
    message: data?.message || fallback,
    fields: data?.errors || {},
    bookingIds: data?.booking_ids || [],
  };
}

const fmtDateTime = (v) => {
  if (!v) return "—";
  const s = String(v).replace("T", " ");
  return s.length >= 16 ? s.slice(0, 16) : s;
};

const REQUEST_TYPE_LABEL = { block: "Block", unblock: "Unblock" };

// ── Block form — direct create (Admin/Director) or request (Instructor) ────
// Same fields either way; only the submit call, success copy and the extra
// "note" field (request mode only, goes to the approver) differ. Direct
// create lands the block as Active immediately; a request creates the same
// block row as "Pending" and waits for an Admin/Director to approve it.
function BlockForm({ mode, facilities, onCancel, onSaved }) {
  const [facilityId, setFacilityId] = useState(facilities[0]?.id || "");
  const [slots, setSlots] = useState([]);
  const [slotId, setSlotId] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [startTime, setStartTime] = useState("");
  const [endTime, setEndTime] = useState("");
  const [reason, setReason] = useState("");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [bookingIds, setBookingIds] = useState([]);
  const [fieldErrors, setFieldErrors] = useState({});

  const fieldError = (key) => {
    const msgs = fieldErrors[key];
    return Array.isArray(msgs) && msgs[0] ? msgs[0] : null;
  };

  // Whenever the selected facility changes, load its slots so the operator
  // can optionally narrow the block to a single slot instead of the whole
  // facility.
  useEffect(() => {
    setSlotId("");
    setSlots([]);
    if (!facilityId) return;
    let on = true;
    getFacility(facilityId)
      .then((f) => on && setSlots(f.slots || []))
      .catch(() => {});
    return () => {
      on = false;
    };
  }, [facilityId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (saving) return;
    setError("");
    setBookingIds([]);
    setFieldErrors({});
    if (!reason.trim()) {
      setFieldErrors({ reason: ["A reason is required."] });
      return;
    }
    setSaving(true);
    try {
      const payload = {
        facility_id: Number(facilityId),
        start_date: startDate,
        end_date: endDate,
        reason: reason.trim(),
      };
      if (slotId) payload.facility_slot_id = Number(slotId);
      if (startTime) payload.start_time = startTime;
      if (endTime) payload.end_time = endTime;
      if (mode === "request" && note.trim()) payload.note = note.trim();

      const result = mode === "direct" ? await createFacilityBlock(payload) : await submitFacilityBlockRequest(payload);
      toast.success(mode === "direct" ? "Facility block created." : result.message || "Block request submitted.");
      onSaved(result);
    } catch (err) {
      const info = errInfo(err, mode === "direct" ? "Could not create the facility block." : "Could not submit the block request.");
      setError(info.message);
      setFieldErrors(info.fields);
      setBookingIds(info.bookingIds);
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {mode === "request" && (
        <p className="text-sm text-gray-500 -mt-1">
          This won't take effect immediately — an Admin/Director must approve it before the facility is actually
          closed to bookings.
        </p>
      )}
      <div>
        <label className={labelClass}>Facility</label>
        <select className={inputClass} value={facilityId} onChange={(e) => setFacilityId(e.target.value)} required>
          {facilities.map((f) => (
            <option key={f.id} value={f.id}>
              {f.facility_name}
            </option>
          ))}
        </select>
        {fieldError("facility_id") && <p className="text-xs text-red-600 mt-1">{fieldError("facility_id")}</p>}
      </div>

      <div>
        <label className={labelClass}>
          Slot <span className="text-gray-400 font-normal">(optional — leave blank to block the whole facility)</span>
        </label>
        <select className={inputClass} value={slotId} onChange={(e) => setSlotId(e.target.value)}>
          <option value="">Whole facility</option>
          {slots.map((s) => (
            <option key={s.id} value={s.id}>
              {s.slot_code}
            </option>
          ))}
        </select>
        {fieldError("facility_slot_id") && (
          <p className="text-xs text-red-600 mt-1">{fieldError("facility_slot_id")}</p>
        )}
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className={labelClass}>Start date</label>
          <input
            type="date"
            className={inputClass}
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            required
          />
          {fieldError("start_date") && <p className="text-xs text-red-600 mt-1">{fieldError("start_date")}</p>}
        </div>
        <div>
          <label className={labelClass}>End date</label>
          <input
            type="date"
            className={inputClass}
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            required
          />
          {fieldError("end_date") && <p className="text-xs text-red-600 mt-1">{fieldError("end_date")}</p>}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className={labelClass}>
            Start time <span className="text-gray-400 font-normal">(optional)</span>
          </label>
          <input type="time" className={inputClass} value={startTime} onChange={(e) => setStartTime(e.target.value)} />
          {fieldError("start_time") && <p className="text-xs text-red-600 mt-1">{fieldError("start_time")}</p>}
        </div>
        <div>
          <label className={labelClass}>
            End time <span className="text-gray-400 font-normal">(optional)</span>
          </label>
          <input type="time" className={inputClass} value={endTime} onChange={(e) => setEndTime(e.target.value)} />
          {fieldError("end_time") && <p className="text-xs text-red-600 mt-1">{fieldError("end_time")}</p>}
        </div>
      </div>
      <p className="text-xs text-gray-400 -mt-2">
        Leave both times blank to block the full day(s); set both together to block only that time window each day.
      </p>

      <div>
        <label className={labelClass}>Reason</label>
        <textarea
          className={`${inputClass} resize-none`}
          rows={2}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          required
        />
        {fieldError("reason") && <p className="text-xs text-red-600 mt-1">{fieldError("reason")}</p>}
      </div>

      {mode === "request" && (
        <div>
          <label className={labelClass}>
            Note to approver <span className="text-gray-400 font-normal">(optional)</span>
          </label>
          <textarea
            className={`${inputClass} resize-none`}
            rows={2}
            placeholder="Any extra context for the Admin/Director reviewing this."
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
        </div>
      )}

      {error && (
        <div className="text-sm text-red-600">
          <p>{error}</p>
          {bookingIds.length > 0 && (
            <p className="mt-1 text-xs">Conflicting booking ID{bookingIds.length === 1 ? "" : "s"}: {bookingIds.join(", ")}</p>
          )}
        </div>
      )}

      <div className="flex gap-3 pt-1">
        <button type="button" onClick={onCancel} className={`${btnSecondary} flex-1`}>
          Cancel
        </button>
        <button type="submit" disabled={saving} className={`${btnPrimary} flex-1`}>
          {saving ? (mode === "direct" ? "Creating…" : "Submitting…") : mode === "direct" ? "Create Block" : "Submit Request"}
        </button>
      </div>
    </form>
  );
}

// ── Direct unblock (Admin/Director) — reason required ───────────────────
function DirectUnblockModal({ block, onClose, onDone }) {
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (saving || !reason.trim()) return;
    setSaving(true);
    setError("");
    try {
      await deleteFacilityBlock(block.id, reason.trim());
      toast.success("Facility block removed.");
      onDone(block.id);
    } catch (err) {
      setError(errInfo(err, "Could not remove the facility block.").message);
      setSaving(false);
    }
  };

  return (
    <Modal title="Unblock Facility" onClose={onClose}>
      <form onSubmit={handleSubmit} className="space-y-4">
        <p className="text-sm text-gray-500">
          Removing the block on <span className="font-semibold">{block.facility_name}</span> ({block.start_date} –{" "}
          {block.end_date}) reopens it to bookings immediately. A reason is required.
        </p>
        <div>
          <label className={labelClass}>Reason</label>
          <textarea
            className={`${inputClass} resize-none`}
            rows={3}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            required
            autoFocus
          />
        </div>
        {error && <p className="text-sm text-red-600">{error}</p>}
        <div className="flex gap-3 pt-1">
          <button type="button" onClick={onClose} className={`${btnSecondary} flex-1`}>
            Cancel
          </button>
          <button type="submit" disabled={saving || !reason.trim()} className={`${btnPrimary} flex-1`}>
            {saving ? "Removing…" : "Remove Block"}
          </button>
        </div>
      </form>
    </Modal>
  );
}

// ── Request unblock (Instructor) — goes into the approval queue ─────────
function RequestUnblockModal({ block, onClose, onDone }) {
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (saving || !note.trim()) return;
    setSaving(true);
    setError("");
    try {
      const r = await submitFacilityUnblockRequest({ facility_block_id: block.id, note: note.trim() });
      toast.success(r.message || "Unblock request submitted.");
      onDone(block.id);
    } catch (err) {
      setError(errInfo(err, "Could not submit the unblock request.").message);
      setSaving(false);
    }
  };

  return (
    <Modal title="Request Unblock" onClose={onClose}>
      <form onSubmit={handleSubmit} className="space-y-4">
        <p className="text-sm text-gray-500">
          Asks an Admin/Director to lift the block on <span className="font-semibold">{block.facility_name}</span> (
          {block.start_date} – {block.end_date}). The block stays in place until approved.
        </p>
        <div>
          <label className={labelClass}>Note</label>
          <textarea
            className={`${inputClass} resize-none`}
            rows={3}
            placeholder="e.g. Maintenance finished. Please unblock for bookings."
            value={note}
            onChange={(e) => setNote(e.target.value)}
            required
            autoFocus
          />
        </div>
        {error && <p className="text-sm text-red-600">{error}</p>}
        <div className="flex gap-3 pt-1">
          <button type="button" onClick={onClose} className={`${btnSecondary} flex-1`}>
            Cancel
          </button>
          <button type="submit" disabled={saving || !note.trim()} className={`${btnPrimary} flex-1`}>
            {saving ? "Submitting…" : "Submit Request"}
          </button>
        </div>
      </form>
    </Modal>
  );
}

// ── Blocks tab ────────────────────────────────────────────────────────────
function BlocksTab({ canCreate, canDelete, canSubmitRequests }) {
  const [blocks, setBlocks] = useState([]);
  const [facilities, setFacilities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [facilityFilter, setFacilityFilter] = useState("");
  const [date, setDate] = useState("");
  // Defaults to "all" (not the backend's own "Active" default) so a
  // just-submitted Pending block request is visible here right away, not
  // just in the Block Requests tab — the dropdown lets it be narrowed back
  // down once Rejected/Removed rows start piling up.
  const [blockStatusFilter, setBlockStatusFilter] = useState("all");
  const [blockFormMode, setBlockFormMode] = useState(null); // null | "direct" | "request"
  const [unblockingBlock, setUnblockingBlock] = useState(null); // direct-unblock target
  const [requestingBlock, setRequestingBlock] = useState(null); // request-unblock target

  const fetchBlocks = useCallback(() => {
    setLoading(true);
    const params = { block_status: blockStatusFilter };
    if (facilityFilter) params.facility_id = facilityFilter;
    if (date) params.date = date;
    listFacilityBlocks(params)
      .then(setBlocks)
      .catch((err) => toast.error(err?.response?.data?.message || "Could not load facility blocks."))
      .finally(() => setLoading(false));
  }, [facilityFilter, date, blockStatusFilter]);

  useEffect(() => {
    fetchBlocks();
  }, [fetchBlocks]);

  useEffect(() => {
    listFacilities()
      .then(setFacilities)
      .catch(() => toast.error("Could not load facilities."));
  }, []);

  const onBlockFormSaved = () => {
    setBlockFormMode(null);
    fetchBlocks();
  };

  const onUnblocked = (blockId) => {
    setUnblockingBlock(null);
    setBlocks((list) => list.filter((b) => b.id !== blockId));
  };

  const onRequested = (blockId) => {
    setRequestingBlock(null);
    setBlocks((list) => list.map((b) => (b.id === blockId ? { ...b, pending_unblock_request: true } : b)));
  };

  const { page, setPage, totalPages, pageItems: pagedBlocks } = usePagination(blocks, {
    resetKey: `${facilityFilter}|${date}|${blockStatusFilter}`,
  });

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="flex flex-wrap gap-3">
          <select
            className={`${inputClass} w-auto`}
            value={facilityFilter}
            onChange={(e) => setFacilityFilter(e.target.value)}
          >
            <option value="">All facilities</option>
            {facilities.map((f) => (
              <option key={f.id} value={f.id}>
                {f.facility_name}
              </option>
            ))}
          </select>
          <select
            className={`${inputClass} w-auto`}
            value={blockStatusFilter}
            onChange={(e) => setBlockStatusFilter(e.target.value)}
          >
            <option value="all">All statuses</option>
            <option value="Active">Active</option>
            <option value="Pending">Pending</option>
            <option value="Rejected">Rejected</option>
            <option value="Removed">Removed</option>
          </select>
          <input type="date" className={`${inputClass} w-auto`} value={date} onChange={(e) => setDate(e.target.value)} />
          {date && (
            <button onClick={() => setDate("")} className="text-xs font-semibold text-gray-500 hover:text-gray-800">
              Clear date
            </button>
          )}
        </div>
        {canCreate && (
          <button
            onClick={() => setBlockFormMode("direct")}
            disabled={facilities.length === 0}
            className={`${btnPrimary} disabled:opacity-50 disabled:cursor-not-allowed`}
          >
            <Icon path={ICONS.plus} className="w-4 h-4" width={2} />
            Add Block
          </button>
        )}
        {!canCreate && canSubmitRequests && (
          <button
            onClick={() => setBlockFormMode("request")}
            disabled={facilities.length === 0}
            className={`${btnPrimary} disabled:opacity-50 disabled:cursor-not-allowed`}
          >
            <Icon path={ICONS.plus} className="w-4 h-4" width={2} />
            Request Block
          </button>
        )}
      </div>

      <div className="bg-white rounded-2xl shadow-sm p-5">
        {loading ? (
          <div className="flex flex-col items-center justify-center py-16 gap-3">
            <Spinner />
            <p className="text-sm text-gray-400">Loading facility blocks…</p>
          </div>
        ) : blocks.length === 0 ? (
          <EmptyState icon={ICONS.block} text="No facility blocks found." />
        ) : (
          <div className="overflow-x-auto -mx-5 px-5">
            <table className="w-full text-left min-w-[860px]">
              <thead>
                <tr className="text-xs text-gray-400 border-b border-gray-100">
                  <th className="py-2 pr-3 font-medium">Facility</th>
                  <th className="py-2 px-3 font-medium">Dates</th>
                  <th className="py-2 px-3 font-medium">Time</th>
                  <th className="py-2 px-3 font-medium">Reason</th>
                  <th className="py-2 px-3 font-medium">Status</th>
                  <th className="py-2 pl-3 font-medium text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {pagedBlocks.map((b) => (
                  <tr key={b.id} className="border-b border-gray-50 last:border-0">
                    <td className="py-3 pr-3">
                      <p className="text-sm font-semibold" style={{ color: TOKENS.NAVY }}>
                        {b.facility_name}
                      </p>
                      <p className="text-xs text-gray-400">{b.slot_code || "Whole facility"}</p>
                    </td>
                    <td className="py-3 px-3 text-sm text-gray-600 whitespace-nowrap">
                      {b.start_date} – {b.end_date}
                    </td>
                    <td className="py-3 px-3 text-sm text-gray-600 whitespace-nowrap">
                      {b.start_time && b.end_time ? `${b.start_time.slice(0, 5)}–${b.end_time.slice(0, 5)}` : "All day"}
                    </td>
                    <td className="py-3 px-3 text-sm text-gray-600 truncate max-w-xs">{b.reason || "—"}</td>
                    <td className="py-3 px-3">
                      <Pill>{b.block_status}</Pill>
                      {b.pending_block_request && <p className="text-[11px] text-amber-600 mt-1">Block request pending</p>}
                      {b.pending_unblock_request && <p className="text-[11px] text-amber-600 mt-1">Unblock request pending</p>}
                    </td>
                    <td className="py-3 pl-3">
                      <div className="flex items-center justify-end gap-3">
                        {canDelete && b.can_delete && (
                          <button
                            onClick={() => setUnblockingBlock(b)}
                            className="inline-flex items-center gap-1 text-xs font-semibold text-red-500 hover:text-red-700"
                          >
                            <Icon path={ICONS.x} className="w-4 h-4" width={2} />
                            Remove
                          </button>
                        )}
                        {!canDelete && canSubmitRequests && b.block_status === "Active" && !b.is_past && (
                          <button
                            onClick={() => setRequestingBlock(b)}
                            disabled={b.pending_unblock_request}
                            className="inline-flex items-center gap-1 text-xs font-semibold disabled:opacity-40 disabled:cursor-not-allowed"
                            style={{ color: TOKENS.PRIMARY }}
                          >
                            {b.pending_unblock_request ? "Requested" : "Request Unblock"}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {totalPages > 1 && (
          <div className="pt-4 mt-2 border-t border-gray-100">
            <Pagination page={page} totalPages={totalPages} onPageChange={setPage} />
          </div>
        )}
      </div>

      {blockFormMode && (
        <Modal title={blockFormMode === "direct" ? "Add Facility Block" : "Request Facility Block"} onClose={() => setBlockFormMode(null)}>
          <BlockForm mode={blockFormMode} facilities={facilities} onCancel={() => setBlockFormMode(null)} onSaved={onBlockFormSaved} />
        </Modal>
      )}

      {unblockingBlock && (
        <DirectUnblockModal block={unblockingBlock} onClose={() => setUnblockingBlock(null)} onDone={onUnblocked} />
      )}

      {requestingBlock && (
        <RequestUnblockModal block={requestingBlock} onClose={() => setRequestingBlock(null)} onDone={onRequested} />
      )}
    </div>
  );
}

// ── Block Requests tab ────────────────────────────────────────────────────
// Two different views of the same resource depending on what the logged-in
// user can do: an Admin/Director with "view_facility_block_requests" sees the
// full queue — both block and unblock requests, filterable by either — with
// Approve/Reject actions gated separately by
// "approve_facility_block_requests"/"reject_facility_block_requests"; an
// Instructor with only "submit_facility_block_requests" sees a read-only
// history of their own submissions instead.
function BlockRequestsTab({ canView, canApprove, canReject }) {
  const [statusFilter, setStatusFilter] = useState("Pending");
  const [typeFilter, setTypeFilter] = useState("");
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [acting, setActing] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [rejectionNote, setRejectionNote] = useState("");

  const fetchRequests = useCallback(() => {
    setLoading(true);
    const params = { request_type: typeFilter };
    const fetcher = canView
      ? listFacilityBlockRequests({ ...params, status: statusFilter })
      : listMyFacilityBlockRequests(params);
    fetcher
      .then(setRequests)
      .catch((err) => toast.error(err?.response?.data?.message || "Could not load block requests."))
      .finally(() => setLoading(false));
  }, [canView, statusFilter, typeFilter]);

  useEffect(() => {
    fetchRequests();
  }, [fetchRequests]);

  const openDetail = (id) => {
    setSelectedId(id);
    setDetail(null);
    setRejecting(false);
    setRejectionNote("");
    setDetailLoading(true);
    getFacilityBlockRequest(id)
      .then(setDetail)
      .catch((err) => {
        toast.error(err?.response?.data?.message || "Could not load this request's details.");
        setSelectedId(null);
      })
      .finally(() => setDetailLoading(false));
  };

  const closeDetail = () => {
    setSelectedId(null);
    setDetail(null);
  };

  const handleApprove = async () => {
    if (!detail || acting) return;
    const confirmMsg =
      detail.request_type === "block"
        ? "Approve this block request? The facility will close to bookings for the requested period immediately."
        : "Approve this unblock request? The facility block will be removed immediately.";
    if (!window.confirm(confirmMsg)) return;
    setActing(true);
    try {
      const r = await approveFacilityBlockRequest(detail.id);
      toast.success(r.message || "Request approved.");
      closeDetail();
      fetchRequests();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Could not approve this request.");
    } finally {
      setActing(false);
    }
  };

  const handleReject = async (e) => {
    e.preventDefault();
    if (!detail || acting || !rejectionNote.trim()) return;
    setActing(true);
    try {
      const r = await rejectFacilityBlockRequest(detail.id, rejectionNote.trim());
      toast.success(r.message || "Request rejected.");
      closeDetail();
      fetchRequests();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Could not reject this request.");
    } finally {
      setActing(false);
    }
  };

  const fb = detail?.facility_block;

  const { page, setPage, totalPages, pageItems: pagedRequests } = usePagination(requests, {
    resetKey: `${statusFilter}|${typeFilter}`,
  });

  return (
    <div>
      <div className="flex flex-wrap items-center gap-3 mb-4">
        {canView && (
          <select className={`${inputClass} w-auto`} value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <option value="Pending">Pending</option>
            <option value="Approved">Approved</option>
            <option value="Rejected">Rejected</option>
            <option value="">All</option>
          </select>
        )}
        <select className={`${inputClass} w-auto`} value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}>
          <option value="">All types</option>
          <option value="block">Block</option>
          <option value="unblock">Unblock</option>
        </select>
      </div>

      <div className="bg-white rounded-2xl shadow-sm p-5">
        {loading ? (
          <div className="flex flex-col items-center justify-center py-16 gap-3">
            <Spinner />
            <p className="text-sm text-gray-400">Loading block requests…</p>
          </div>
        ) : requests.length === 0 ? (
          <EmptyState icon={ICONS.block} text="No block requests found." />
        ) : (
          <div className="overflow-x-auto -mx-5 px-5">
            <table className="w-full text-left min-w-[820px]">
              <thead>
                <tr className="text-xs text-gray-400 border-b border-gray-100">
                  <th className="py-2 pr-3 font-medium">Type</th>
                  <th className="py-2 px-3 font-medium">Facility</th>
                  <th className="py-2 px-3 font-medium">Dates</th>
                  {canView && <th className="py-2 px-3 font-medium">Requested by</th>}
                  <th className="py-2 px-3 font-medium">Submitted</th>
                  <th className="py-2 px-3 font-medium">Status</th>
                  <th className="py-2 pl-3 font-medium text-right">Action</th>
                </tr>
              </thead>
              <tbody>
                {pagedRequests.map((r) => (
                  <tr key={r.id} className="border-b border-gray-50 last:border-0">
                    <td className="py-3 pr-3">
                      <Pill>{REQUEST_TYPE_LABEL[r.request_type] || r.request_type}</Pill>
                    </td>
                    <td className="py-3 px-3">
                      <p className="text-sm font-semibold" style={{ color: TOKENS.NAVY }}>
                        {r.facility_block?.facility_name}
                      </p>
                      <p className="text-xs text-gray-400">{r.facility_block?.slot_code || "Whole facility"}</p>
                    </td>
                    <td className="py-3 px-3 text-sm text-gray-600 whitespace-nowrap">
                      {r.facility_block?.start_date} – {r.facility_block?.end_date}
                    </td>
                    {canView && (
                      <td className="py-3 px-3 text-sm text-gray-600 whitespace-nowrap">{r.requested_by_name}</td>
                    )}
                    <td className="py-3 px-3 text-xs text-gray-500 whitespace-nowrap">{fmtDateTime(r.created_at)}</td>
                    <td className="py-3 px-3">
                      <Pill>{r.status}</Pill>
                    </td>
                    <td className="py-3 pl-3 text-right">
                      <button
                        onClick={() => openDetail(r.id)}
                        className="inline-flex items-center justify-center w-8 h-8 rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-50"
                      >
                        <Icon path={ICONS.eye} className="w-4 h-4" width={1.8} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {totalPages > 1 && (
          <div className="pt-4 mt-2 border-t border-gray-100">
            <Pagination page={page} totalPages={totalPages} onPageChange={setPage} />
          </div>
        )}
      </div>

      {selectedId && (
        <Modal title="Block Request" onClose={closeDetail} size="lg">
          {detailLoading || !detail ? (
            <div className="flex flex-col items-center justify-center py-14 gap-3">
              <Spinner />
              <p className="text-sm text-gray-400">Loading…</p>
            </div>
          ) : (
            <div className="space-y-5">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-base font-bold truncate" style={{ color: TOKENS.NAVY }}>
                    {fb?.facility_name}
                  </p>
                  <p className="text-xs text-gray-400 truncate">{fb?.slot_code || "Whole facility"}</p>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <Pill>{REQUEST_TYPE_LABEL[detail.request_type] || detail.request_type}</Pill>
                  <Pill>{detail.status}</Pill>
                </div>
              </div>

              <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4">
                {[
                  ["Dates", fb ? `${fb.start_date} – ${fb.end_date}` : null],
                  ["Time", fb ? (fb.start_time && fb.end_time ? `${fb.start_time.slice(0, 5)}–${fb.end_time.slice(0, 5)}` : "All day") : null],
                  ["Block reason", fb?.reason],
                  ["Requested by", detail.requested_by_name],
                  ["Submitted at", fmtDateTime(detail.created_at)],
                  ["Note", detail.note],
                  ["Reviewed by", detail.reviewed_by_name],
                  ["Reviewed at", fmtDateTime(detail.reviewed_at)],
                ]
                  .filter(([, v]) => v !== null && v !== undefined && v !== "")
                  .map(([label, value]) => (
                    <div key={label} className="min-w-0 sm:col-span-2">
                      <dt className="text-xs text-gray-400">{label}</dt>
                      <dd className="text-sm font-medium text-gray-700 break-words" style={{ color: TOKENS.NAVY }}>
                        {value}
                      </dd>
                    </div>
                  ))}
              </dl>

              {detail.status === "Pending" && (canApprove || canReject) && (
                <div className="pt-4 border-t border-gray-100 space-y-3">
                  {!rejecting ? (
                    <div className="flex flex-wrap gap-3">
                      {canApprove && (
                        <button
                          type="button"
                          onClick={handleApprove}
                          disabled={acting}
                          className={`${btnPrimary} !w-auto px-6`}
                        >
                          {acting ? "Approving…" : "Approve"}
                        </button>
                      )}
                      {canReject && (
                        <button
                          type="button"
                          onClick={() => setRejecting(true)}
                          disabled={acting}
                          className="inline-flex items-center justify-center gap-2 border border-red-200 text-red-600 hover:bg-red-50 font-semibold rounded-lg text-sm px-6 py-2.5 transition"
                        >
                          Reject
                        </button>
                      )}
                    </div>
                  ) : (
                    <form onSubmit={handleReject} className="space-y-3">
                      <div>
                        <label className={labelClass}>Rejection note</label>
                        <textarea
                          className={`${inputClass} resize-none`}
                          rows={2}
                          value={rejectionNote}
                          onChange={(e) => setRejectionNote(e.target.value)}
                          required
                          autoFocus
                        />
                      </div>
                      <div className="flex gap-3">
                        <button
                          type="submit"
                          disabled={acting || !rejectionNote.trim()}
                          className="inline-flex items-center justify-center gap-2 bg-red-600 hover:bg-red-700 text-white font-semibold rounded-lg text-sm px-6 py-2.5 transition"
                        >
                          {acting ? "Rejecting…" : "Confirm Reject"}
                        </button>
                        <button type="button" onClick={() => setRejecting(false)} className={`${btnSecondary} !w-auto px-6`}>
                          Cancel
                        </button>
                      </div>
                    </form>
                  )}
                </div>
              )}
            </div>
          )}
        </Modal>
      )}
    </div>
  );
}

// ── Page ────────────────────────────────────────────────────────────────
export default function FacilityBlocksManagement() {
  const { can } = useAuth();
  const canCreate = can("create_facility_blocks");
  const canDelete = can("delete_facility_blocks");
  // Modern permission names (see facilityBlockRequestService.js); OR'd with
  // the legacy unblock-only names so a role seeded before
  // ConfigureFacilityBlockRequestFlowSeeder ran still works.
  const canSubmitRequests = can("submit_facility_block_requests") || can("submit_facility_unblock_requests");
  const canViewRequests = can("view_facility_block_requests") || can("view_facility_unblock_requests");
  const canApproveRequests = can("approve_facility_block_requests") || can("approve_facility_unblock_requests");
  const canRejectRequests = can("reject_facility_block_requests") || can("reject_facility_unblock_requests");

  const tabs = [
    { key: "blocks", label: "Blocks" },
    (canViewRequests || canSubmitRequests) && { key: "requests", label: "Block Requests" },
  ].filter(Boolean);

  const [activeTab, setActiveTab] = useState("blocks");

  return (
    <div>
      <div className="mb-5">
        <h1 className="text-xl font-bold" style={{ color: TOKENS.NAVY }}>
          Facility Blocks
        </h1>
        <p className="text-sm text-gray-500">Close a facility (or one slot) to bookings for a date/time range.</p>
      </div>

      {tabs.length > 1 && (
        <div className="flex gap-1 mb-5 border-b border-gray-200">
          {tabs.map((t) => (
            <button
              key={t.key}
              onClick={() => setActiveTab(t.key)}
              className={`px-4 py-2.5 text-sm font-semibold border-b-2 transition ${
                activeTab === t.key ? "border-current" : "border-transparent text-gray-500 hover:text-gray-700"
              }`}
              style={activeTab === t.key ? { color: TOKENS.PRIMARY } : undefined}
            >
              {t.label}
            </button>
          ))}
        </div>
      )}

      {activeTab === "blocks" && (
        <BlocksTab canCreate={canCreate} canDelete={canDelete} canSubmitRequests={canSubmitRequests} />
      )}
      {activeTab === "requests" && (canViewRequests || canSubmitRequests) && (
        <BlockRequestsTab canView={canViewRequests} canApprove={canApproveRequests} canReject={canRejectRequests} />
      )}
    </div>
  );
}
